import { addSeconds, differenceInMilliseconds } from 'date-fns';
import { ITokenResponse } from '../redux/Auth/AuthModel';
import { authApiEndPoints } from '../constants/ApiEndPoints';

export const constants = {
  ACCESS_TOKEN: 'access_token',
  ID_TOKEN: 'id_token',
  TOKEN_TYPE: 'token_type',
  EXPIRES_IN: 'expires_in',
  EXPIRY_TIME: 'expiry_time',
  REFRESH_TOKEN: 'refresh_token'
};

/**
 * Retrieves an item from localStorage
 * @param key - The key to retrieve
 * @returns The stored value or empty string if not found
 */
export const getAuthItem = (key: string): string => {
  const item = window.localStorage.getItem(key);
  return item || '';
};

/**
 * Stores an item in localStorage
 * @param key - The key to store
 * @param value - The value to store
 */
export const setAuthItem = (key: string, value: string) => {
  window.localStorage.setItem(key, value);
};

/**
 * Removes all authentication tokens from localStorage
 */
export const removeTokens = () => {
  Object.values(constants).forEach((key) => {
    window.localStorage.removeItem(key);
  });
};

/**
 * Saves authentication tokens to localStorage
 * @param data - The token response containing access token, ID token, and expiry information
 */
export const saveToken = (data: ITokenResponse) => {
  setAuthItem(constants.ACCESS_TOKEN, data.access_token);
  setAuthItem(constants.ID_TOKEN, data.id_token);
  setAuthItem(constants.TOKEN_TYPE, data.token_type);
  setAuthItem(constants.EXPIRES_IN, data.expires_in.toString());
  setAuthItem(constants.REFRESH_TOKEN, data.refresh_token);

  const expiryTime = addSeconds(new Date(), data.expires_in);
  setAuthItem(constants.EXPIRY_TIME, expiryTime.toISOString());
};

/**
 * Checks if the current token has expired
 * @returns boolean indicating if the token is expired
 */
export const isTokenExpired = (): boolean => {
  const expiryTime = getAuthItem(constants.EXPIRY_TIME);
  if (!expiryTime) return true;

  const endDate = new Date(expiryTime);
  const currentDate = new Date();
  return differenceInMilliseconds(endDate, currentDate) < 0;
};

/**
 * Checks if the user is currently authenticated
 * @returns boolean indicating if the user is authenticated
 */
export const isUserAuthenticated = (): boolean => {
  const accessToken = getAuthItem(constants.ID_TOKEN);
  return Boolean(accessToken && !isTokenExpired());
};

/**
 * Extracts the authorization code from the current URL
 * @returns The authorization code or null if not present
 */
export const getCodeFromUrl = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('code');
};

/**
 * Redirects the user to the authentication page
 */
export const redirectToAuthPage = (): void => {
  if((process.env.REACT_APP_CALLBACK_URL || '').includes(location.hostname)){
    sessionStorage.setItem('preserveUrl', location.pathname + location.search);
  }
  window.location.href = authApiEndPoints.loginURL;
};